// Clone data before loading a writer or rendering an image. Dates and Chart.js
// scriptable options are preserved, but subsequent card changes cannot alter rows.
export function cloneDashboardExport (value) {
  if (value instanceof Date) { return new Date(value.getTime()) }
  if (Array.isArray(value)) { return value.map(cloneDashboardExport) }
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, cloneDashboardExport(item)]))
  }
  return value
}

export function sortExportRows (rows, sortBy = [], sortDesc = []) {
  const keys = Array.isArray(sortBy) ? sortBy : [sortBy]
  const descending = Array.isArray(sortDesc) ? sortDesc : [sortDesc]
  return [...rows].sort((left, right) => {
    for (let index = 0; index < keys.length; index++) {
      const a = left[keys[index]]
      const b = right[keys[index]]
      let comparison = 0
      if (a == null && b != null) { comparison = -1 } else if (b == null && a != null) { comparison = 1 } else if (typeof a === 'number' && typeof b === 'number') { comparison = a - b } else { comparison = String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' }) }
      if (comparison) { return descending[index] ? -comparison : comparison }
    }
    return 0
  })
}

export function resolveDashboardChart (component, ChartJS) {
  if (component?.chart?.canvas) {
    return component.chart
  }
  // vue-chartjs's typed wrapper forwards a function ref, which is not reliably
  // exposed in Vue 2.7. Resolve only this component's canvas through Chart.js.
  const element = component?.$el
  const canvas = element?.tagName === 'CANVAS' ? element : element?.querySelector?.('canvas')
  return canvas ? ChartJS.getChart(canvas) || null : null
}

export function chartExportSheet (chart, { name = 'Data', labelHeading = 'Period', valueLabel = 'Värde', valueFormat = 'decimal' } = {}) {
  if (!chart) { return { name, columns: [], rows: [] } }
  const datasets = chart.data.datasets
    .map((dataset, index) => ({ dataset, index }))
    .filter(({ index }) => chart.isDatasetVisible(index))
  const columns = [
    { key: 'label', label: labelHeading },
    ...datasets.map(({ dataset, index }) => ({ key: `series${index}`, label: String(dataset.label ?? valueLabel), format: valueFormat }))
  ]
  const rows = (chart.data.labels || []).flatMap((label, index) => {
    if (!chart.getDataVisibility(index)) { return [] }
    const row = { label: Array.isArray(label) ? label.join(' ') : label }
    datasets.forEach(({ dataset, index: seriesIndex }) => {
      const point = dataset.data[index]
      const value = point && typeof point === 'object' ? point.y ?? point.r ?? null : point
      row[`series${seriesIndex}`] = valueFormat === 'percent' && typeof value === 'number' ? value / 100 : value
    })
    return [row]
  })
  return { name, columns, rows }
}

export function dashboardExportFilename (snapshot, extension) {
  const dateValues = (snapshot.parameters || [])
    .map(parameter => String(parameter.value ?? ''))
    .filter(value => /^\d{4}-\d{2}-\d{2}$/.test(value))
  const period = [...new Set(dateValues)].slice(0, 2).join('_')
  const date = (snapshot.exportedAt || new Date().toISOString()).slice(0, 10)
  const name = String(snapshot.title || 'Dashboard').normalize('NFC')
    .replace(/[<>:"/\\|?*]/g, '-').replace(/\p{Cc}/gu, '-').replace(/\s+/g, ' ').replace(/[. ]+$/g, '').trim().slice(0, 100)
  return `${name || 'Dashboard'}_${period || date}.${extension}`
}

export async function downloadDashboardWorkbook (snapshot) {
  const [XLSX, { createDashboardWorkbook }] = await Promise.all([
    import(/* webpackChunkName: "dashboard-excel" */ 'xlsx'),
    import('./dashboardWorkbook')
  ])
  XLSX.writeFile(createDashboardWorkbook(snapshot, XLSX), dashboardExportFilename(snapshot, 'xlsx'), { bookType: 'xlsx', compression: true })
}

function wrapText (context, text, maxWidth) {
  const lines = []
  let line = ''
  // Wrapping individual characters also handles long category names without spaces.
  for (const character of String(text)) {
    if (character === '\n' || (line && context.measureText(line + character).width > maxWidth)) {
      lines.push(line.trimEnd())
      line = character === '\n' ? '' : character
    } else {
      line += character
    }
  }
  if (line) { lines.push(line.trimEnd()) }
  return lines
}

// Re-render a detached Chart.js chart at export resolution. The displayed chart,
// including its animation/hover state and legend selections, is never modified.
export function createDashboardChartCanvas (chart, snapshot) {
  if (!chart?.canvas || !chart.width || !chart.height) { throw new Error('Diagrammet är inte klart för export') }
  const canvas = document.createElement('canvas')
  const width = Math.max(1200, chart.width)
  const height = Math.round(width * chart.height / chart.width)
  canvas.width = width
  canvas.height = height
  const data = cloneDashboardExport(chart.data)
  data.datasets.forEach((dataset, index) => { dataset.hidden = !chart.isDatasetVisible(index) })
  const options = cloneDashboardExport(chart.config.options || {})
  options.responsive = false
  options.animation = false
  options.devicePixelRatio = 2
  options.events = []
  options.color = '#263238'
  options.plugins = options.plugins || {}
  options.plugins.tooltip = { ...options.plugins.tooltip, enabled: false }
  if (options.plugins.legend !== false) {
    options.plugins.legend = { ...options.plugins.legend, labels: { ...options.plugins.legend?.labels, color: '#263238' } }
  }
  Object.values(options.scales || {}).forEach(scale => {
    scale.ticks = { ...scale.ticks, color: '#455a64' }
    if (scale.title) { scale.title = { ...scale.title, color: '#263238' } }
  })
  let exportChart
  try {
    exportChart = new chart.constructor(canvas, { type: chart.config.type, data, options, plugins: chart.config.plugins || [] })
    for (let index = 0; index < (chart.data.labels || []).length; index++) {
      if (!chart.getDataVisibility(index)) { exportChart.toggleDataVisibility(index) }
    }
    exportChart.update('none')

    const output = document.createElement('canvas')
    const context = output.getContext('2d')
    const pixelRatio = 2
    const padding = 32
    const maxTextWidth = width - padding * 2
    context.font = 'bold 24px sans-serif'
    const titleLines = wrapText(context, snapshot.title, maxTextWidth)
    context.font = '16px sans-serif'
    const parameters = (snapshot.parameters || []).map(({ label, value }) => `${label}: ${Array.isArray(value) ? value.join(', ') : value ?? '—'}`).join(' · ')
    const parameterLines = wrapText(context, parameters, maxTextWidth)
    const headingHeight = padding * 2 + titleLines.length * 32 + parameterLines.length * 24
    output.width = width * pixelRatio
    output.height = (headingHeight + height + 44) * pixelRatio
    context.scale(pixelRatio, pixelRatio)
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, width, output.height / pixelRatio)
    context.fillStyle = '#263238'
    context.font = 'bold 24px sans-serif'
    titleLines.forEach((line, index) => context.fillText(line, padding, padding + 24 + index * 32))
    context.font = '16px sans-serif'
    parameterLines.forEach((line, index) => context.fillText(line, padding, padding + titleLines.length * 32 + 24 + index * 24))
    context.drawImage(canvas, 0, headingHeight, width, height)
    context.font = '14px sans-serif'
    context.fillStyle = '#546e7a'
    context.fillText(`${snapshot.exportLabels?.exported ?? 'Exporterad'}: ${snapshot.exportedAt} · ${snapshot.exportLabels?.source ?? 'Källa'}: CampingOnline`, padding, headingHeight + height + 28)
    return output
  } finally {
    exportChart?.destroy()
  }
}

export function downloadDashboardChart (chart, snapshot) {
  // Capture synchronously, before the card can change while toBlob is pending.
  const canvas = createDashboardChartCanvas(chart, snapshot)
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) { return reject(new Error('Bilden kunde inte skapas')) }
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = dashboardExportFilename(snapshot, 'png')
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      resolve()
    }, 'image/png')
  })
}
